import { useEffect, useState } from "react";
import type { UpdateFormAdminTypes } from "../../types/admin/admin.form";
import buttonLoadingAnimation from "../../animations/buttonLoading.json";
import Lottie from "lottie-react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  updateAdmin,
  getAdmin,
  clearSelectedAdmin,
} from "../../redux/slices/adminSlice";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../utils/errorHandler";

export interface UpdateAdminProps {
  adminId: string;
  handleClose: () => void;
}

export const UpdateAdmin = ({ adminId, handleClose }: UpdateAdminProps) => {
  const dispatch = useAppDispatch();
  const { selectedAdmin } = useAppSelector((state) => state.admin);
  const [formData, setFormData] = useState<UpdateFormAdminTypes>({
    name: "",
    password: "",
    isActive: true,
  });

  useEffect(() => {
    dispatch(getAdmin(adminId));
    return () => {
      dispatch(clearSelectedAdmin());
    };
  }, [adminId, dispatch]);

  useEffect(() => {
    if (selectedAdmin) {
      setFormData({
        name: selectedAdmin.name,
        password: "",
        isActive: selectedAdmin.isActive,
      });
    }
  }, [selectedAdmin]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "isActive" ? value === "true" : value,
    }));
  };

  const [loading, setLoading] = useState<boolean>(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name?.trim()) {
      toast.error("Tên admin không được để trống");
      return;
    }
    if (formData.password && formData.password.length < 6) {
      toast.error("Mật khẩu phải có ít nhất 6 ký tự");
      return;
    }
    setLoading(true);
    try {
      const payload: UpdateFormAdminTypes = { ...formData };
      if (!payload.password) {
        delete payload.password;
      }
      await dispatch(updateAdmin({ id: adminId, data: payload })).unwrap();
      toast.success("Cập nhật admin thành công");
      handleClose();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  if (!selectedAdmin) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-3 z-50">
        <div className="p-5 bg-white rounded-lg w-full max-w-md">
          <p className="text-sm text-gray-500 text-center">
            Đang tải thông tin...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-3 z-50">
      <div className="p-5 bg-white rounded-lg w-full max-w-md space-y-2">
        <div className="flex items-center justify-center">
          <p className="text-lg font-semibold text-gray-700">
            Cập nhật admin
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-2">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Tên admin
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="p-2 focus:outline-none focus:border-blue-500 transition-colors duration-300 border border-gray-200 rounded-xl w-full placeholder:text-sm placeholder:tracking-tighter placeholder:text-gray-400"
              placeholder="Tên admin"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Mật khẩu mới (để trống nếu không đổi)
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="p-2 focus:outline-none focus:border-blue-500 transition-colors duration-300 border border-gray-200 rounded-xl w-full placeholder:text-sm placeholder:tracking-tighter placeholder:text-gray-400"
              placeholder="Để trống nếu không đổi"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Trạng thái
            </label>
            <select
              name="isActive"
              value={String(formData.isActive)}
              onChange={handleChange}
              className="border border-gray-200 rounded-xl p-2 cursor-pointer text-sm text-gray-700 focus:outline-none focus:border-blue-500 transition-colors duration-300"
            >
              <option value="true">Hoạt động</option>
              <option value="false">Ngừng hoạt động</option>
            </select>
          </div>
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={handleClose}
              className="p-2 text-white text-sm bg-blue-500 hover:bg-blue-600 rounded-lg cursor-pointer"
            >
              Quay lại
            </button>
            <div className="flex items-center gap-2">
              {loading ? (
                <Lottie
                  animationData={buttonLoadingAnimation}
                  loop={true}
                  className="h-10 w-20"
                />
              ) : (
                <button
                  type="submit"
                  className="p-2 text-white text-sm bg-green-500 hover:bg-green-600 rounded-lg cursor-pointer"
                >
                  Cập nhật
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
